import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RowClickedEvent, type ColDef, SortChangedEvent } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { AiTopicRow, SortState } from '../types';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { MANAGEMENT_GRID_MODULES, MANAGEMENT_GRID_THEME, createManagementGridOptions, toSortState } from '../management-grid';

@Component({
  selector: 'app-ai-topics-table',
  imports: [AgGridAngular],
  template: `<ag-grid-angular style="width: 100%;" [rowData]="rows" [columnDefs]="colDefs" (rowClicked)="onRowClicked($event)"
    [modules]="modules" [theme]="theme" [domLayout]="'autoHeight'" [suppressCellFocus]="true"
    [gridOptions]="gridOptions" (sortChanged)="onSortChanged($event)"></ag-grid-angular>`,
})
export class AiTopicsTableComponent {
  @Input() rows: AiTopicRow[] = [];
  @Output() rowClicked = new EventEmitter<AiTopicRow>();
  @Output() sortChanged = new EventEmitter<SortState>();

  readonly theme = MANAGEMENT_GRID_THEME;
  readonly modules = MANAGEMENT_GRID_MODULES;
  readonly gridOptions = createManagementGridOptions();
  colDefs: ColDef[] = this.getColDef();

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as AiTopicRow);
  };

  public onSortChanged(event: SortChangedEvent): void {
    const sort = toSortState(event);
    if (sort) {
      this.sortChanged.emit(sort);
    }
  }

  private getColDef(): ColDef[] {
    return [
      {
        colId: 'name',
        headerName: 'Topic',
        flex: 2,
        valueGetter: (params: any) => params.data?.name,
      },
      {
        headerName: 'Brands',
        colId: 'brands',
        cellRenderer: AgTooltipCellComponent,
        // Same presentation as the brands table's Accounts column: joined keys, full list on hover
        valueGetter: (params: any) => (params.data?.brandKeys ?? []).join(', '),
        sortable: false,
      },
      {
        headerName: 'Prompts',
        colId: 'prompts',
        // Derived client-side from the prompts list — not a server sort key
        sortable: false,
        maxWidth: 110,
        // null = prompts fetch failed: unknown, not zero
        valueGetter: (params: any) => (params.data?.promptCount === null || params.data?.promptCount === undefined ? '—' : `${params.data.promptCount}`),
      },
      {
        headerName: 'Status',
        colId: 'status',
        sortable: false,
        maxWidth: 230,
        // Derived: a topic runs while any brand carrying it has AI Presence on — its attached
        // brands, plus competitors that inherited it.
        // "Pipeline only" = prompts exist in Snowflake but no console topic matches this name.
        // Exact only after the first replace-mode resync has pruned legacy brand keys from
        // AI_PRESENCE_BRANDS: before that, a legacy BRAND_TO_TOPIC row can still make it run.
        valueGetter: (params: any) => {
          if (params.data?.managed === false) {
            return 'Pipeline only — no console topic';
          }
          return params.data?.collecting ? 'Collecting' : 'Not collecting';
        },
      },
    ];
  }
}
